/**
 * Diagnostics tab — shapes and pure helpers (the client mirror of services/diagnostics.py).
 *
 * The SERVER decides which catalogue entries a viewer may run (GET /diagnostics/catalogue
 * returns only those) and re-checks role, tenancy, params, rate limit and the one-per-device
 * lock on every run. Nothing here enforces anything: these helpers only shape what the tab
 * shows (param hints, the result meta line, the bundle filename). Kept free of React so
 * `npm run test:unit` covers them.
 */

/** One parameter of a catalogue entry; `pattern` is FULL-matched by the server. */
export interface DiagnosticParam {
  name: string;
  pattern: string;
  description: string;
  example: string;
}

/** A catalogue entry as GET /api/v2/diagnostics/catalogue returns it. */
export interface DiagnosticEntry {
  name: string;
  title: string;
  description: string;
  kinds: string[];
  transport: "gateway_ssh" | "switch_cli" | "owgw_state";
  commands: string[];
  timeout_s: number;
  max_output_bytes: number;
  min_role: string;
  redactions: string[];
  params: DiagnosticParam[];
  read_only: boolean;
  service_account: boolean;
  in_bundle: boolean;
  /** >0: a log tail; truncation keeps the newest lines. */
  tail_lines: number;
  notes: string;
}

export interface DiagnosticCatalogue {
  kind: string | null;
  items: DiagnosticEntry[];
  count: number;
  bundle: { available: boolean; entries: string[] };
}

/** POST /devices/<serial>/diagnostics/<name> body (success AND transport failure). */
export interface DiagnosticResult {
  name: string;
  serial: string;
  ok: boolean;
  output: string;
  truncated: boolean;
  redactions: number;
  duration_ms: number;
  audit_id: number;
  params?: Record<string, string>;
  error: { code: string; message: string } | null;
}

/** Operator wording for each transport: what the run actually touches. */
export const TRANSPORT_LABEL: Record<DiagnosticEntry["transport"], string> = {
  gateway_ssh: "Gateway SSH",
  switch_cli: "Switch CLI",
  owgw_state: "Controller state",
};

/**
 * Transports that open a live session on the device (gateway shell, switch CLI). The server
 * requires org_admin or above for every entry on these (CLI_RELAY_MIN_ROLE, owner decision
 * 2026-10-09 "option A"); a site operator only ever gets the controller-state reads.
 */
export const CLI_RELAY_TRANSPORTS: ReadonlySet<DiagnosticEntry["transport"]> =
  new Set(["gateway_ssh", "switch_cli"]);

/**
 * True when the viewer's catalogue for a gateway/switch carries NO device-session entry, i.e.
 * a site operator: the tab then explains that the deeper checks need an org admin, instead of
 * looking like the device simply has nothing to offer. Access points never have such entries.
 */
export function deviceSessionChecksHidden(kind: string, items: DiagnosticEntry[]): boolean {
  if (kind !== "gateway" && kind !== "switch") return false;
  return !items.some((e) => CLI_RELAY_TRANSPORTS.has(e.transport));
}

/**
 * Client-side pre-check of a param value against the server's pattern (full match), so the
 * operator sees "1/10, not 10" before a round trip. The server re-validates regardless.
 * A pattern this browser cannot compile is treated as "let the server decide" (true).
 */
export function paramValid(param: DiagnosticParam, value: string): boolean {
  if (!value || value.length > 32) return false;
  try {
    return new RegExp(`^(?:${param.pattern})$`).test(value);
  } catch {
    return true;
  }
}

/** "32 KiB" style size for the truncation note. */
export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${Math.round(n / (1024 * 1024))} MiB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KiB`;
  return `${n} B`;
}

/** "1.2 s" / "340 ms". */
export function formatDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.max(0, Math.round(ms))} ms`;
}

/** The facts strip under an output: duration, redactions, truncation, audit reference. */
export function resultFacts(result: DiagnosticResult, entry?: DiagnosticEntry): string[] {
  const facts = [formatDuration(result.duration_ms)];
  if (result.redactions > 0) {
    facts.push(`${result.redactions} secret${result.redactions === 1 ? "" : "s"} hidden`);
  }
  if (result.truncated) {
    facts.push(entry ? `cut at ${formatBytes(entry.max_output_bytes)}` : "output cut");
  }
  facts.push(`audit #${result.audit_id}`);
  return facts;
}

/** Filename from a Content-Disposition header, with a safe fallback. */
export function bundleFilename(disposition: string | null, serial: string): string {
  // Only a plain `name.json` is accepted: no path separators, no leading dot.
  const m = /filename="?([A-Za-z0-9_-][A-Za-z0-9._-]*\.json)"?(?:;|$)/.exec(disposition ?? "");
  return m?.[1] ?? `meridian-diag-${serial}.json`;
}

/** Human message for a refused run (status + server body), in the interface's voice. */
export function refusalMessage(status: number, body: unknown): string {
  const b = (body ?? {}) as { error?: unknown; code?: string; retry_after?: number };
  const err = typeof b.error === "string" ? b.error
    : (b.error as { message?: string } | undefined)?.message;
  if (status === 429) return `Too many runs. Try again in ${b.retry_after ?? 60} s.`;
  if (status === 409 && b.code === "busy") return "Another diagnostic is running on this device. Wait for it to finish.";
  if (status === 403) return "Your role cannot run this diagnostic on this device.";
  if (status === 503) return "The audit log is unavailable, so the output was withheld. Try again shortly.";
  return err || `The diagnostic failed (HTTP ${status}).`;
}
